/**
 * DirectImageDisplay.js - A simple and direct approach to display images in 3D space
 * This creates a plane with the image texture and positions it behind the model
 */
import * as THREE from 'three';

export class DirectImageDisplay {
    constructor(scene, camera) {
        this.scene = scene;
        this.camera = camera;
        this.plane = null;
        this.isVisible = false;
        
        // Create a simple plane for displaying images
        this.createPlane();
    }
    
    /**
     * Create a plane for displaying images
     */
    createPlane() {
        // Create a default texture
        const canvas = document.createElement('canvas');
        canvas.width = 512;
        canvas.height = 512;
        const context = canvas.getContext('2d');
        context.fillStyle = 'rgba(0, 0, 0, 0)';
        context.fillRect(0, 0, canvas.width, canvas.height);
        
        const defaultTexture = new THREE.CanvasTexture(canvas);
        
        // Create material with transparency
        const material = new THREE.MeshBasicMaterial({
            map: defaultTexture,
            transparent: true,
            opacity: 0,
            side: THREE.DoubleSide
        });
        
        // Create plane geometry (1:1 aspect ratio to start)
        const geometry = new THREE.PlaneGeometry(1, 1);
        
        // Create mesh
        this.plane = new THREE.Mesh(geometry, material);
        
        // Initial position (will be adjusted when showing an image)
        this.plane.position.set(0, 0, 0);
        
        // Don't add to scene yet - we'll add it when showing an image
    }
    
    /**
     * Show an image on the plane
     * @param {string} imageUrl - URL of the image to display
     * @return {Promise<boolean>} Success status
     */
    async showImage(imageUrl) {
        if (!imageUrl) {
            console.error('No image URL provided');
            return false;
        }
        
        console.log(`Displaying image: ${imageUrl}`);
        
        try {
            // Load the texture
            const texture = await this.loadTexture(imageUrl);
            
            // Update plane geometry based on aspect ratio
            const aspectRatio = texture.image.width / texture.image.height;
            this.plane.geometry.dispose();
            
            // Make the plane larger for better visibility
            const height = 4.0;
            const width = height * aspectRatio;
            this.plane.geometry = new THREE.PlaneGeometry(width, height);
            
            // Update the material with the new texture
            this.plane.material.map = texture;
            this.plane.material.needsUpdate = true;
            
            // Position the plane behind the model
            this.positionPlane();
            
            // Add to scene if not already there
            if (!this.isVisible) {
                this.scene.add(this.plane);
                this.isVisible = true;
            }
            
            // Animate appearance
            this.animateAppearance();
            
            return true;
        } catch (error) {
            console.error('Error showing image:', error);
            return false;
        }
    }
    
    /**
     * Load a texture with proper CORS handling
     * @param {string} url - Image URL
     * @return {Promise<THREE.Texture>} Loaded texture
     */
    loadTexture(url) {
        return new Promise((resolve, reject) => {
            // Use the server proxy to load the image and avoid CORS issues
            const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(url)}`;
            console.log('Loading image through proxy:', proxyUrl);
            
            // Create a THREE.js texture loader with CORS settings
            const loader = new THREE.TextureLoader();
            loader.crossOrigin = 'anonymous';
            
            // Load the texture through our proxy
            loader.load(
                proxyUrl,
                (texture) => {
                    console.log('Texture loaded successfully!');
                    resolve(texture);
                },
                (progress) => {
                    // Log loading progress
                    if (progress.lengthComputable) {
                        const percent = (progress.loaded / progress.total) * 100;
                        console.log(`Loading: ${Math.round(percent)}%`);
                    }
                },
                (error) => {
                    console.error('Error loading texture:', error);
                    
                    // Create a fallback texture
                    const canvas = document.createElement('canvas');
                    canvas.width = 512;
                    canvas.height = 512;
                    const context = canvas.getContext('2d');
                    
                    // Create a gradient background
                    const gradient = context.createLinearGradient(0, 0, canvas.width, canvas.height);
                    gradient.addColorStop(0, '#6a11cb');
                    gradient.addColorStop(1, '#2575fc');
                    context.fillStyle = gradient;
                    context.fillRect(0, 0, canvas.width, canvas.height);
                    
                    // Add error text
                    context.font = '32px Arial';
                    context.fillStyle = 'white';
                    context.textAlign = 'center';
                    context.fillText('Image could not be loaded', canvas.width/2, canvas.height/2);
                    
                    const fallbackTexture = new THREE.CanvasTexture(canvas);
                    resolve(fallbackTexture);
                }
            );
        });
    }
    
    /**
     * Position the plane behind the model
     */
    positionPlane() {
        // Simple fixed position behind and above the model
        // This ensures it's visible from most camera angles
        this.plane.position.set(0, 4.5, -5);
        
        // Make the plane face the camera
        this.plane.lookAt(this.camera.position);
        
        console.log('Positioned plane at:', this.plane.position);
    }
    
    /**
     * Animate the plane appearing
     */
    animateAppearance() {
        // Ensure material is set for animation
        this.plane.material.opacity = 0;
        this.plane.material.transparent = true;
        
        // Add a slight scaling effect too
        const originalScale = 1.0;
        const startScale = 0.8;
        this.plane.scale.set(startScale, startScale, startScale);
        
        // Animation parameters
        const duration = 700; // ms
        const startTime = performance.now();
        
        // Animation function
        const animate = (time) => {
            const elapsed = time - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // Use ease-out cubic for smooth animation
            const easeProgress = 1 - Math.pow(1 - progress, 3);
            
            // Update opacity
            this.plane.material.opacity = easeProgress;
            
            // Update scale
            const currentScale = startScale + (originalScale - startScale) * easeProgress;
            this.plane.scale.set(currentScale, currentScale, currentScale);
            
            // Continue animation if not complete
            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };
        
        // Start animation
        requestAnimationFrame(animate);
    }
    
    /**
     * Hide the image plane
     */
    hideImage() {
        if (!this.isVisible) return;
        
        // Animation parameters
        const duration = 300; // ms
        const startTime = performance.now();
        const startOpacity = this.plane.material.opacity;
        
        // Animation function
        const animate = (time) => {
            const elapsed = time - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // Update opacity
            this.plane.material.opacity = startOpacity * (1 - progress);
            
            // Update scale with a subtle shrinking effect
            const scale = 1.0 - 0.2 * progress;
            this.plane.scale.set(scale, scale, scale);
            
            // Continue animation if not complete
            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                // Remove from scene when animation completes
                this.scene.remove(this.plane);
                this.isVisible = false;
                console.log('Image hidden');
            }
        };
        
        // Start animation
        requestAnimationFrame(animate);
    }
    
    /**
     * Update the plane to face the camera when camera moves
     */
    update() {
        if (this.isVisible) {
            this.plane.lookAt(this.camera.position);
        }
    }
    
    /**
     * Set up keyboard listener to close image with Escape key
     */
    setupKeyboardEvents() {
        window.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && this.isVisible) {
                this.hideImage();
            }
        });
    }
}